import { computed, Inject, Injectable, OnDestroy, Signal, signal, WritableSignal } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType } from '@azure/msal-browser';
import { CLIENT_ID } from '@environments/environment';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { User } from '../classes/User.class';
import { Role } from '../models/enums/Role.enum';
import { Companies, StoreFeature, StoreFeatureConfiguration, StorefeatureConfigurations } from '../models/types/Configuration/Settings.type';
import { StoreConfiguration } from '../models/types/Configuration/StoreConfiguration.type';
import { UserConfiguration } from '../models/types/Configuration/UserConfiguration.type';
import { RoleService } from './api/role.service';
import { DynatraceService } from './dynatrace.service';
import { LoaderService } from './loader.service';
import { SharedService } from './shared.service';
import { FiltersService } from './filters.service';
import { StoreFeaturesList } from '../models/constants/store-services.constant';
import { Feature } from '../models/enums/Feature.enum';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {

  private readonly USER_CONFIGURATION_STORAGE_KEY = `app.user.configuration.${CLIENT_ID}`;
  private readonly STORE_CONFIGURATION_STORAGE_KEY = `app.store.configuration.${CLIENT_ID}`;
  private readonly REDIRECT_TO_MAIN_STORAGE_KEY = `app.redirect.to.main.${CLIENT_ID}`;
  private readonly _destroying$ = new Subject<void>();

  private _user: User | undefined = undefined;
  public userSession$ = new BehaviorSubject<User | undefined>(this._user);
  public get user(): User | undefined {
    return this._user;
  }

  private _store: WritableSignal<StoreConfiguration | undefined> = signal(undefined);
  public get store(): StoreConfiguration | undefined {
    return this._store();
  }

  public storeSession$ = new BehaviorSubject<StoreConfiguration | undefined>(this._store());
  public get isStoreSelected(): boolean {
    return !!this._store();
  }

  private _availableStores: StoreConfiguration[] = [];
  public get availableStore(): StoreConfiguration[] {
    return this._availableStores;
  }

  private _companyCodes: Companies = [];
  public get companyCodes(): Companies {
    return this._companyCodes;
  }

  public storeFeatures: WritableSignal<StoreFeature[]> = signal([]);

  private _storeFeatureConfiguration: WritableSignal<StoreFeatureConfiguration | undefined> = signal(undefined);
  public storeFeatureConfiguration: Signal<StoreFeatureConfiguration | undefined> = computed(() => this._storeFeatureConfiguration());

  constructor(
    private sharedService: SharedService,
    private readonly msalService: MsalService,
    private readonly roleService: RoleService,
    private readonly loaderService: LoaderService,
    private readonly dynatraceService: DynatraceService,
    private readonly broadcastService: MsalBroadcastService,
    @Inject(MSAL_GUARD_CONFIG) private readonly msalGuardConfig: MsalGuardConfiguration
  ) {
    this.monitorAuthEvents();
    this.loadStore();
  }

  private monitorAuthEvents() {
    //Observer per il loader della login
    this.broadcastService.inProgress$
      .pipe(
        tap((val) => {
          // console.log(val);
          if (val !== 'none') {
            // this.loaderService.show();
          } else {
            // this.loaderService.hide();
          }
        }),
        takeUntil(this._destroying$)
      )
      .subscribe();

    //Observer per la gestione delle varie fasi della login
    this.broadcastService.msalSubject$
      .pipe(
        tap((msg: EventMessage) => {
          this.log(msg);
          switch (msg.eventType) {
            case EventType.INITIALIZE_END: {
              // this.acquireTokenSilent();
              if (this.needToRedirectToMainPage()) {
                this.sharedService.navigateTo_MainPage();
                this.removeRedirectToMainInLocalStorage();
              }
              break;
            }
            case EventType.LOGIN_START: {
              //Ho iniziato il redirect verso la pagina di autenticazione MSAL
              this.loaderService.show();
              break;
            }
            case EventType.LOGIN_SUCCESS:
            case EventType.LOGOUT_SUCCESS: {
              //Sono appena tornato dall pagina di autenticazione MSAL
              this.sharedService.navigateTo_MainPage();
              break;
            }
            case EventType.LOGIN_FAILURE: {
              //La login è fallita
              this.sharedService.navigateTo_ErrorPage(() => console.log('error_page'));
              break;
            }
            case EventType.HANDLE_REDIRECT_END: {
              //Il giro completo di login è terminato
              this.setUser();
              break;
            }
            case EventType.ACQUIRE_TOKEN_FAILURE: {
              if (window.location.pathname === "/") {
                this.setRedirectToMainInLocalStorage();
              }
              // if (!this.needToRedirectToMainPage()) {
              //   //Si è verificato qualche problema, rifare la login
              //   this.loaderService.hide();
              //   this.removeUserConfigurationInLocalStorage();
              //   this.sharedService.navigateTo_ErrorPage(() => console.log('error_page'));
              // }
              break;
            }
            /**
             * ACQUIRETOKENFROMNETWORKSTART e ACQUIRETOKENFAILURE si verificano quando
             * ho l'utente già salvato nello storage ma il token è scaduto.
             * Occorre salvarsi un flag nello storage in modo tale che, quando si riatterra
             *
             *
             */
            case EventType.ACQUIRE_TOKEN_NETWORK_START: {
              // this.setRedirectToMainInLocalStorage();
              break;
            }
            case EventType.LOGOUT_SUCCESS: {
              this.sharedService.navigateTo_HomePage();
              break;
            }
          }
        }),
        takeUntil(this._destroying$)
      )
      .subscribe();
  }

  log(msg: EventMessage) {
    console.log(
      `%cMSAL%c ► %c${msg.eventType.split(":")[1].toUpperCase()}`,
      "color: darkorange; font-weight: bold;",
      "color: gold;",
      "",
    );
  }

  /**
   * Setta i dati dell'utente.
   *
   * Questo metodo prende l'account dall'istanza di msalService, esegue l'API per ottenere
   * la configurazione dell'utente loggato da cui è possibile ricavare il ruolo e lo setta.
   *
   */
  setUser() {
    let msalAccountInfo = this.msalService.instance.getAllAccounts()[0];
    console.log("MSAL Accounts:", this.msalService.instance.getAllAccounts().map(account => account.username).toString());
    console.log(`%c[MSAL]%c ACCOUNTS (${this.msalService.instance.getAllAccounts().length}) = %c${this.msalService.instance.getAllAccounts().map(account => account.username).toString()}`,
      "color: black; font-weight: bold;",
      "color: gray; text-style: italic;",
      "color: black;",)
    if (!msalAccountInfo) {
      console.error("Errore nel reperimento dell'account utente");
      // this.logout();
    } else {
      this._user = new User(msalAccountInfo);
      this.dynatraceService.identifyUser(this._user.username);
      if (window.location.pathname === "/") {
        this.sharedService.navigateTo_MainPage();
      }
    }
  }

  /**
   * Setta il ruolo dell'utente.
   *
   * Questo metodo ricava dalla configurazione dell'utente il ruolo e lo setta.
   *
   * @param userConfiguration - Configurazione dell'utente
   */
  public setUserRole(userConfiguration: UserConfiguration) {
    //Ricavo gli store a cui l'utente può accedere
    this._availableStores = userConfiguration.storesConfigurations;
    //Ricavo il ruolo dell'utente in base alla response dalla getRole()
    const role = this.roleService.parseRole(userConfiguration);
    this._user!.setRole(role);
    this.dynatraceService.logRoleName(role);
    //Emitto la modifica dell'utente per notificare l'aggiornamento
    this.userSession$.next(this._user);
    this.loadSettings();
  }

  /**
   * Setta lo store per l'utente loggato.
   *
   * @param store - Store da settare
   */
  public setStore(store: StoreConfiguration) {
    if (store.idStore !== this._store()?.idStore) {
      //Quando cambia lo store resetto il filtro "OnlyInSTock" settato nel local storage
      FiltersService.removeOnlyInStockOptionInLocalStorage();
    }
    this._store.set(store);
    this.storeSession$.next(store);
    this.setStoreInLocalStorage(store);
    this.dynatraceService.logStoreName(store.nameStore, store.codeStore);
  }

  getDefaultStore(): StoreConfiguration | undefined {
    return this._availableStores[0]
  }

  public removeStore() {
    this._store.set(undefined);
    this.storeSession$.next(undefined);
    this.removeStoreInLocalStorage();
  }

  loadStore() {
    let store = this.getStoreInLocalStorage();
    if (store) {
      this.setStore(store);
    }
  }

  public login() {
    this.msalService.loginRedirect();
  }

  public logout() {
    this.removeUserConfigurationInLocalStorage();
    this.msalService.logout();
  }

  private setStoreInLocalStorage(store: StoreConfiguration) {
    localStorage.setItem(this.STORE_CONFIGURATION_STORAGE_KEY, JSON.stringify(store));
  }

  public getStoreInLocalStorage(): StoreConfiguration | null {
    let store = localStorage.getItem(this.STORE_CONFIGURATION_STORAGE_KEY);
    if (store) {
      return JSON.parse(store) as StoreConfiguration;
    }
    return null;
  }

  private removeStoreInLocalStorage(): void {
    localStorage.removeItem(this.STORE_CONFIGURATION_STORAGE_KEY);
  }

  private setUserConfigurationInLocalStorage(userConfiguration: UserConfiguration): void {
    localStorage.setItem(this.USER_CONFIGURATION_STORAGE_KEY, JSON.stringify(userConfiguration));
  }

  public getUserConfigurationInLocalStorage(): Role | null {
    return localStorage.getItem(this.USER_CONFIGURATION_STORAGE_KEY) as Role;
  }

  private removeUserConfigurationInLocalStorage(): void {
    localStorage.removeItem(this.USER_CONFIGURATION_STORAGE_KEY);
  }

  private setRedirectToMainInLocalStorage(): void {
    localStorage.setItem(this.REDIRECT_TO_MAIN_STORAGE_KEY, "true");
  }

  private removeRedirectToMainInLocalStorage(): void {
    localStorage.removeItem(this.REDIRECT_TO_MAIN_STORAGE_KEY);
  }

  private needToRedirectToMainPage(): boolean {
    return !!localStorage.getItem(this.REDIRECT_TO_MAIN_STORAGE_KEY);
  }

  private acquireTokenSilent() {
    const request = {
      scopes: ["User.Read"],
      account: this.msalService.instance.getAllAccounts()[0]
    };

    this.msalService.instance.acquireTokenSilent(request)
      .then(response => {
        //Il token è stato acquisito, non devo fare nulla
      })
      .catch(error => {
        console.error("Errore nell'acquisizione del token:", error);
        //Se non ho acquisito il token rieffettuo la login
        this.login();
      });
  }

  loadSettings() {
    this.storeFeatures.set([]);
    this.loaderService.show();
    this.roleService.getSettings().subscribe({
      next: (settings) => {
        this._companyCodes = settings.companyCodes || [];
        let storeFeatureConfig = settings.storeFeatureConfigurations?.find(storeConfig => storeConfig.idStore === this._store()?.idStore);
        this._storeFeatureConfiguration.set(storeFeatureConfig);
        let availableFeatures = storeFeatureConfig?.availableFeatures;
        if (availableFeatures) {
          availableFeatures.forEach(feature => {
            let storeFeature = StoreFeaturesList.get(feature);
            if (!storeFeature) {
              console.warn(`Feature ${feature} not found in StoreFeaturesList`);
            } else {
              this.storeFeatures.update(features => [...features, storeFeature]);
            }
          });
        }
        this.loaderService.hide();
      },
      error: (err) => {
        console.log(err);
        this.loaderService.hide();
      }
    })
  }

  ngOnDestroy() {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
